import * as React from 'react';
import { styled } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { CloseIcon } from 'webapp/components/icons';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { translate, useMatchLargeViewport } from '@avensia/excite';
import { bareListDecorator } from 'webapp/decorators/list';
import { selector } from '@glitz/core';

type PropType = {
  selectedFacets: Excite.Esales.FacetApiModel[] | undefined;
  onFacetChange: (
    facet: Excite.Esales.FacetApiModel,
    value: Excite.Esales.FacetValueApiModel | Excite.Esales.RangeApiModel,
  ) => void;
  clearAllFacets: () => void;
};

export const SelectedFacets = ({ selectedFacets, onFacetChange, clearAllFacets }: PropType) => {
  const isDesktop = useMatchLargeViewport();
  const buttonSize = isDesktop ? FieldSize.Small : FieldSize.XSmall;

  const onClickActiveFacet = React.useCallback(
    (value: Excite.Esales.FacetValueApiModel, facet: Excite.Esales.FacetApiModel) => {
      value.selected = false;
      onFacetChange(facet, value);
    },
    [onFacetChange],
  );
  const boolFacets = ['instock'];
  return (
    <Base>
      <li>
        <ResetButton size={buttonSize} as={Theme.Transparent} onClick={clearAllFacets} disableRippleEffect>
          {translate(t => t.productListing.clearFilterButtonText)} <Close />
        </ResetButton>
      </li>
      {selectedFacets?.map(f =>
        f.values.map(v => (
          <li key={f.attribute + v.text}>
            <FilterButton onClick={() => onClickActiveFacet(v, f)} as={Theme.Monochrome} size={buttonSize}>
              {boolFacets.includes(f.attribute) ? f.text : v.displayText} <Close />
            </FilterButton>
          </li>
        )),
      )}
    </Base>
  );
};

const Base = styled.ul(
  bareListDecorator,
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.xs },
    overflowX: 'auto',
  }),
  {
    gridArea: 'selectedFacets',
    display: 'flex',
    gap: t => t.sharedSpacing.xs,
  },
  createMinLargeViewportDecorator({
    flexWrap: 'wrap',
  }),
  selector('::-webkit-scrollbar', {
    display: 'none',
  }),
);

const Close = styled(CloseIcon, {
  marginRight: -3,
  width: 16,
  flexShrink: 0,
});

const FilterButton = styled(
  Button,
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.xs },
  }),
  {
    flexShrink: 0,
    gap: t => t.sharedSpacing.xs,
    lineHeight: 'normal',
    fontSize: t => t.sharedFontSize.xs,
    whiteSpace: 'nowrap',
    padding: { x: 11 },
  },
);

const ResetButton = styled(
  FilterButton,
  createMaxSmallViewportDecorator({
    padding: { left: 0, right: t => t.sharedSpacing.xs }, //Media query is needed to override the padding from FilterButton
  }),
  {
    padding: { left: 0, right: t => t.sharedSpacing.xs },
    color: t => t.negative.color,
  },
);
